import React, { useState } from 'react';
import styled from 'styled-components';
import {
    SliderRoot,
    SliderTrack,
    SliderRange,
    SliderThumb,
    ValueLabel
} from './HBSlider.styles';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface Props {
    /** Unique identifier for the slider; used as the native `name` attribute and `aria-label`. */
    id: string;
    /** Minimum selectable value on the track. */
    minValue: number;
    /** Maximum selectable value on the track. */
    maxValue: number;
    /** Increment between selectable values on the slider track; defaults to 1. */
    stepSize?: number;
    /** Callback fired with the new numeric value whenever the slider moves (or after debounce if `debounceMs` is set). */
    onChange: (value: number) => void;
    /** Current controlled value. Defaults to 0 when omitted. Changes to it are reflected by the thumb, including when `debounceMs` is set (an echo of the slider's own debounced `onChange` is ignored). */
    value?: number;
    /** When true, the slider track and thumb are non-interactive and visually muted. */
    disabled?: boolean;
    /** When set, delays onChange calls by this many milliseconds while the user is dragging; the slider updates visually in real time using internal state, which re-syncs whenever `value` changes. */
    debounceMs?: number;
    /** CSS class forwarded to the root container element. */
    className?: string;
}

function useHBSlider({
    value,
    debounceMs,
    onChange
}: Pick<Props, 'value' | 'debounceMs' | 'onChange'>) {
    const [innerValue, setInnerValue] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    const [lastEmitted, setLastEmitted] = useState<number>();

    if (value !== prevValue) {
        setPrevValue(value);
        setLastEmitted(undefined);
        if (value !== lastEmitted) {
            setInnerValue(value);
        }
    }

    const debouncedOnChange = useDebouncedCallback(val => {
        setLastEmitted(val);
        onChange(val);
    }, debounceMs ?? 0);

    if (!debounceMs) {
        return { value, onChange };
    }

    return {
        value: innerValue,
        onChange: (val: number) => {
            setInnerValue(val);
            debouncedOnChange(val);
        }
    };
}

const Container = styled.div`
    position: relative;
`;

const HBSlider = ({
    id,
    testIdName,
    testId,
    onChange,
    minValue,
    maxValue,
    stepSize,
    value = 0,
    disabled,
    debounceMs,
    className
}: Props & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const { value: _value, onChange: _onChange } = useHBSlider({
        value,
        debounceMs,
        onChange
    });

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'slider-wrapper',
        componentName: 'HBSlider'
    });

    const handleValueChange = (values: number[]) => {
        _onChange(values[0]);
    };

    return (
        <Container className={className} data-testid={finalTestId}>
            <SliderRoot
                value={[_value]}
                onValueChange={handleValueChange}
                max={maxValue}
                min={minValue}
                step={stepSize || 1}
                disabled={disabled}
                name={id}
                aria-label={id}
            >
                <SliderTrack>
                    <SliderRange />
                </SliderTrack>
                <SliderThumb aria-label={`${id} slider`} />
            </SliderRoot>
            <ValueLabel htmlFor={id}>{_value}</ValueLabel>
        </Container>
    );
};

export default HBSlider;
